// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import { get, safeColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Color from "color";
import * as React from "react";
import { useState } from "react";
import styled, { css, withTheme } from "styled-components";

export type NotifyProps = MobiusStyledComponentProps<
    "div",
    {
        /** Text to display as the title of the notify. Will be ignored if `children` are passed. */
        title?: React.ReactNode;
        /** Text to display in the body of the notify. Will be ignored if `children` are passed. */
        body?: React.ReactNode;
        /** The type of message to be displayed, which governs color and iconography. */
        messageType?: "success" | "danger" | "info" | "warning";
        /** Description of type of message to be read to screenreader, to replace the iconography and color in relaying
         * the importance or valence of the message. Defaults to `messageType` value if not passed in.
         * Can be used for translations or additional specificity if necessary. */
        messageTypeString?: string;
        notifyContainerOverrides?: StyledProp;
        iconContainerOverrides?: StyledProp;
        notifyTitleOverrides?: StyledProp;
        notifyBodyOverrides?: StyledProp;
        closeButtonContainerOverrides?: StyledProp;
        closeButtonIconProps?: IconProps;
        showCloseButton?: boolean;
        css?: StyledProp<any>;
    }
>;

export const extendedProps = {
    icons: { success: "CheckCircle", danger: "Cancel", info: "Info", warning: "ReportProblem" },
    background: {
        success: getColor("transparent"),
        danger: getColor("transparent"),
        info: getColor("transparent"),
        warning: getColor("transparent"),
    },
};

export const notifyStyles: NotifyProps = {
    notifyContainerOverrides: undefined,
    iconContainerOverrides: undefined,
    notifyTitleOverrides: undefined,
    notifyBodyOverrides: undefined,
    closeButtonContainerOverrides: undefined,
    closeButtonIconProps: {
        src: "Clear",
        size: 12,
        color: "common.ui1",
        css: css`
            margin: 4px 10px;
            opacity: 0.65;
        `,
    },
};

export const styles = notifyStyles;

const NotifyContainer = styled.div<any>`
    display: flex;
    width: 100%;
    max-width: 100%;
    background: ${getColor("common.background")};
    box-shadow: 0 0 25px 0 rgba(79, 79, 119, 0.18);
    padding: 10px;
    font-size: 13px;
    line-height: 15px;
    color: ${getColor("text.main")};
    border-top: 4px solid ${props => safeColor(resolveColor(props.messageType, props.theme)).toString()};
    ${injectCss}
`;

const IconContainer = styled.div<any>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin: auto 10px auto 0;
    ${injectCss};
`;

const NotifyTitle = styled.div<InjectableCss>`
    margin-right: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-weight: bold;
    width: 100%;
    ${injectCss}
`;

const NotifyBody = styled.div<InjectableCss>`
    display: flex;
    flex-direction: row;
    align-items: center;
    word-break: break-word;
    ${injectCss}
`;

const CloseButtonContainer = styled.div<InjectableCss>`
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
    ${injectCss}
`;

const Notify: React.FC<NotifyProps> = ({
    messageType,
    messageTypeString,
    children,
    title,
    body,
    theme,
    notifyContainerOverrides,
    iconContainerOverrides,
    notifyTitleOverrides,
    notifyBodyOverrides,
    closeButtonContainerOverrides,
    closeButtonIconProps,
    showCloseButton,
    ...otherProps
}: NotifyProps) => {
    const iconSrcByMessageMap = {
        success: "CheckOutlined",
        danger: "Error",
        warning: "Exclamation",
        info: "ContextualHelp",
    };
    const [visible, setVisibility] = useState(true);

    if (visible) {
        return (
            <NotifyContainer
                css={styles.notifyContainerOverrides || notifyContainerOverrides}
                messageType={messageType}
                data-test-selector={`notify${messageType}`}
                role="status"
                {...otherProps}
            >
                {children || (
                    <StyledWrapper
                        css={css`
                            position: relative;
                            display: flex;
                            flex-wrap: nowrap;
                            width: 100%;
                        `}
                    >
                        <IconContainer
                            background={extendedProps.background[messageType || "warning"]}
                            css={styles.iconContainerOverrides || iconContainerOverrides}
                            messageType={messageType}
                            {...otherProps}
                        >
                            <Icon src={extendedProps.icons[messageType || "warning"]} color={messageType} size={20} />
                        </IconContainer>
                        <StyledWrapper
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                flexGrow: 1,
                            }}
                        >
                            {title && (
                                <NotifyTitle css={styles.notifyTitleOverrides || notifyTitleOverrides}>
                                    <Typography data-test-selector="notifyTitle">{title}:</Typography>
                                </NotifyTitle>
                            )}
                            <NotifyBody css={styles.notifyBodyOverrides || notifyBodyOverrides} data-id="notify-body">
                                <VisuallyHidden>{messageTypeString || messageType}</VisuallyHidden>
                                <Typography data-test-selector="notifyBody">{body}</Typography>
                            </NotifyBody>
                        </StyledWrapper>
                        {showCloseButton && (
                            <CloseButtonContainer
                                css={styles.closeButtonContainerOverrides || closeButtonContainerOverrides}
                                onClick={() => setVisibility(!visible)}
                            >
                                <Icon {...styles.closeButtonIconProps} />
                            </CloseButtonContainer>
                        )}
                    </StyledWrapper>
                )}
            </NotifyContainer>
        );
    }
    return <></>;
};

/** @component */
export default withTheme(Notify);
